import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type MarketSummary } from '../lib/api';
import { relativeTime } from '../lib/format';

export function Markets() {
  const markets = useQuery({ queryKey: ['markets'], queryFn: api.markets });

  if (markets.isLoading) return <div className="empty-state">Loading markets…</div>;
  if (markets.isError) return <div className="empty-state">Could not reach the exchange.</div>;

  const list = markets.data ?? [];
  if (list.length === 0) {
    return <div className="empty-state">No markets are open yet.</div>;
  }

  return (
    <>
      <h1 className="question" style={{ marginBottom: 20 }}>Markets</h1>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
        {list.map((m) => (
          <MarketCard key={m.id} market={m} />
        ))}
      </div>
    </>
  );
}

function MarketCard({ market }: { market: MarketSummary }) {
  const price = market.last_price;
  return (
    <Link to={`/markets/${market.id}`} className="market-card">
      <h3>{market.question}</h3>

      <div className="prob">
        <b>{price === null ? '—' : `${price}%`}</b>
        <small>chance of YES</small>
      </div>

      {/*
        A single bar rather than two numbers: the whole point of a binary
        market is that the two sides sum to 100, and a bar shows that at a
        glance in a way that "YES 63 / NO 37" does not.
      */}
      <div className="probbar" role="img" aria-label={price === null ? 'No price yet' : `${price} percent YES`}>
        <i style={{ width: `${price ?? 0}%` }} />
      </div>

      <footer>
        <StatusBadge status={market.status} />
        <span>{market.open_interest.toLocaleString()} contracts</span>
        <span style={{ marginLeft: 'auto' }}>
          {market.status === 'OPEN' ? `closes ${relativeTime(market.closes_at)}` : ''}
        </span>
      </footer>
    </Link>
  );
}

export function StatusBadge({ status }: { status: MarketSummary['status'] }) {
  const tone =
    status === 'OPEN' ? 'open' : status === 'SETTLED' || status === 'VOIDED' ? 'settled' : 'closed';
  const label =
    status === 'PROPOSED' ? 'Resolving' : status === 'CLOSED' ? 'Awaiting result' : status.toLowerCase();
  return <span className={`badge ${tone}`}>{label}</span>;
}
